/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Prompt } from 'react-router-dom/cjs/react-router-dom';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import RecurringPayableScheduleCard from './recurringPaymentCard.component';
import RecurringPayableInfoCard from './recurringPayableInfoCard.component';
import RecurringPayablePaymentInfoCard from './recurringPayablePaymentInfoCard.component';
import RecurringPayableLineItemsCard from './recurringPayableLineItemsCard.component';
import DynamicFilePreviewCard from 'modules/expenses/common/dynamicFilePreviewCard.component';
import RepayAgreementInfoBox from 'modules/expenses/common/repayAgreementInfoBox.component';
import { EXPENSES_PAYEE_TYPE, RECURRING_PAYABLE_STATUS, ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { getCurrencyOptions, isIdEqual } from 'modules/enumerations/enumerationHelpers';
import _ from 'lodash';
import { getEffectiveStatus } from '../recurringPayableConstants';

export const SECTION_SX = {
  width: '100%',
  px: 2,
  py: 2,
  backgroundColor: 'transparent',
  boxShadow: 'none',
  border: 0,
  borderBottom: '1px solid',
  borderColor: 'divider'
};

const HeaderBar = ({
  clients,
  payToVendor,
  statusTitle,
  rpInfo,
  vendorValue,
  authValue,
  normalizeAuthSummaries,
  rpStatusId,
  onCancel,
  authorizationDetails,
  clientRelocationPolicies
}) => {
  const clientId = rpInfo?.recurringPayableInformation?.clientId;
  const showCancel = !isIdEqual(rpStatusId, RECURRING_PAYABLE_STATUS.DRAFT) && !isIdEqual(rpStatusId, RECURRING_PAYABLE_STATUS.CANCELLED);

  return (
    <Box sx={SECTION_SX}>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', p: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: '20px', flexWrap: 'wrap' }}>
          <Box sx={{ mr: '20px' }}>
            <AdvancedSearch
              labelText="Client"
              displayValueAccessorKey="clientName"
              saveIdValueAccessorKey="clientId"
              searchingCriteria={[
                { name: 'clientId', title: 'Client Id' },
                { name: 'legalName', title: 'Legal Name' },
                { name: 'clientCode', title: 'Client Code' }
              ]}
              searchingCriteriaExtensions={[
                { columnName: 'clientId', width: 150 },
                { columnName: 'legalName', width: 250 },
                { name: 'clientCode', width: 175 }
              ]}
              searchingRows={clients}
              id={clientId}
              value={clients.find(c => c.clientId === clientId)?.clientName || ''}
              setIdValue={() => {}}
              isReadOnly
            />
          </Box>
          <Box>
            <Button
              color="secondary"
              variant="contained"
              disabled
              sx={{
                maxHeight: '40px',
                ml: '12px',
                mt: '8px',
                backgroundColor: payToVendor ? '#36939b' : '#ffffff',
                '&:hover': { backgroundColor: payToVendor ? '#15585e' : '#faf8f2' },
                color: payToVendor ? 'ffffff' : '#080600'
              }}
            >
              Pay to Vendor
            </Button>
            <Button
              color="secondary"
              variant="contained"
              disabled
              sx={{
                maxHeight: '40px',
                ml: '12px',
                mt: '8px',
                backgroundColor: !payToVendor ? '#36939b' : '#ffffff',
                '&:hover': { backgroundColor: !payToVendor ? '#15585e' : '#faf8f2' },
                color: !payToVendor ? 'ffffff' : '#080600'
              }}
            >
              Pay to EE
            </Button>
          </Box>
          {payToVendor ? (
            <Box sx={{ ml: '25px', minWidth: 320 }}>
              <AdvancedSearch
                labelText="Vendor"
                displayValueAccessorKey="companyName"
                saveIdValueAccessorKey="vendorId"
                searchingCriteria={[
                  { name: 'vendorId', title: 'Vendor Id' },
                  { name: 'companyName', title: 'Company Name' },
                  { name: 'city', title: 'City' },
                  { name: 'state', title: 'State' }
                ]}
                searchingCriteriaExtensions={[
                  { columnName: 'vendorId', width: 150 },
                  { columnName: 'companyName', width: 250 },
                  { name: 'city', width: 175 },
                  { name: 'state', width: 175 }
                ]}
                searchingRows={[]}
                id={rpInfo?.payee?.vendorId}
                value={vendorValue}
                setIdValue={() => {}}
                isReadOnly
              />
            </Box>
          ) : (
            <Box sx={{ ml: '25px', minWidth: 320 }}>
              <AdvancedSearch
                labelText="Authorization"
                displayValueAccessorKey="transfereeName"
                saveIdValueAccessorKey="fileId"
                searchingCriteria={[
                  { name: 'fileId', title: 'Auth Id' },
                  { name: 'transfereeName', title: 'Full Name' },
                  { name: 'departure', title: 'Departure' },
                  { name: 'destination', title: 'Destination' },
                  { name: 'clientName', title: 'Client' }
                ]}
                searchingCriteriaExtensions={[
                  { columnName: 'fileId', width: 150 },
                  { columnName: 'transfereeName', width: 250 },
                  { name: 'departure', width: 175 },
                  { name: 'destination', width: 175 },
                  { columnName: 'clientName', width: 175 }
                ]}
                searchingRows={normalizeAuthSummaries || []}
                id={rpInfo?.payee?.authorizationId}
                value={authValue}
                setIdValue={() => {}}
                isReadOnly
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: '12px' }}>
          {showCancel && (
            <Button variant="contained" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Box>
            <Box sx={{ mb: '4px', mr: '20px' }}>
              <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem', textAlign: 'center' }}>
                Status
              </Typography>
            </Box>
            <Box sx={{ mb: '4px', mr: '20px' }}>
              <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem', textAlign: 'center' }}>
                {statusTitle}
              </Typography>
            </Box>
          </Box>
          {!payToVendor && <RepayAgreementInfoBox authorizationDetails={authorizationDetails} clientRelocationPolicies={clientRelocationPolicies} />}
        </Box>
      </Box>
    </Box>
  );
};

const shallowNormalize = obj => ({
  ...obj,
  recurringPayableInformation: {
    ...obj?.recurringPayableInformation,
    amount:
      obj?.recurringPayableInformation?.amount != null && obj?.recurringPayableInformation?.amount !== ''
        ? Number(obj.recurringPayableInformation.amount)
        : 0
  }
});

const RecurringPayableDetails = ({
  shouldShowSideDrawer = false,
  recurringPayableDetail,
  recurringPayableId,
  clients = [],
  enumerations,
  banks,
  addresses,
  payeeDetails,
  clientRelocationPolicies,
  expenseCodes,
  normalizeAuthSummaries,
  onUpdateRecurringPayable,
  onSaveLineItem,
  onDeleteLineItem,
  onCopyLineItem,
  history,
  location,
  updateParentRPAttachments,
  updateParentRPLineAttachments,
  setExceptions,
  exceptions
}) => {
  const [rpForm, setRpForm] = useState(recurringPayableDetail || {});
  const [rpState, setRpState] = useState(recurringPayableDetail || {});
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!recurringPayableDetail) return;
    setRpForm(recurringPayableDetail);
    setRpState(recurringPayableDetail);
    setIsDirty(false);
  }, [recurringPayableDetail]);

  const frequencies = useMemo(() => (enumerations?.recurringPayableFrequencies || []).filter(s => s.isActive), [enumerations]);
  const currencies = useMemo(() => (enumerations?.currencies || []).filter(s => s.isActive), [enumerations]);
  const notPayables = useMemo(() => (enumerations?.notPayables || []).filter(s => s.isActive), [enumerations]);
  const voucherTypes = useMemo(() => (enumerations?.voucherTypes || []).filter(s => s.isActive), [enumerations]);
  const batchTypes = useMemo(() => (enumerations?.batchTypes || []).filter(s => s.isActive), [enumerations]);

  const currencyOpts = useMemo(() => getCurrencyOptions(currencies), [currencies]);

  const rpi = rpForm?.recurringPayableInformation || {};
  const statusId = rpi?.recurringPayableStatusId || ZERO_GUID;
  const statusTitle = getEffectiveStatus(rpi);

  const isActive = isIdEqual(statusId, RECURRING_PAYABLE_STATUS.ACTIVE);

  const onCancel = async () => {
    const next = {
      ...rpForm,
      recurringPayableInformation: {
        ...rpi,
        recurringPayableStatusId: RECURRING_PAYABLE_STATUS.CANCELLED
      }
    };

    await onUpdateRecurringPayable(next);
  };

  const isDraft = statusTitle.toLowerCase() === 'draft' || String(statusId).toLowerCase() === String(ZERO_GUID).toLowerCase(); // keep permissive for unset/new
  const isReadOnly = !isDraft;

  const payeeTypeId = rpForm?.payee?.payeeTypeId;
  const payToVendor = String(payeeTypeId || '').toLowerCase() === String(EXPENSES_PAYEE_TYPE.VENDOR).toLowerCase();
  const authOptions = useMemo(() => {
    const relevantAuthIds = [
      rpForm?.payee?.authorizationId,
      ...(rpForm?.recurringPayableLineItems ?? []).map(lineItem => lineItem?.paymentAuthorizationId)
    ].filter(Boolean);

    const seen = new Set();
    return relevantAuthIds
      .filter(authId => {
        const key = String(authId).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(authId => {
        const matchedAuth = (normalizeAuthSummaries ?? []).find(
          auth => String(auth?.fileId ?? auth?.authorizationId ?? auth?.authId ?? '').toLowerCase() === String(authId).toLowerCase()
        );
        const fileId = matchedAuth?.fileId ?? matchedAuth?.authorizationId ?? matchedAuth?.authId ?? authId;
        const transfereeName = matchedAuth?.transfereeName ?? matchedAuth?.fullName ?? matchedAuth?.authorizationName ?? '';
        return {
          value: fileId,
          label: transfereeName ? `${transfereeName} (${fileId})` : String(fileId)
        };
      });
  }, [normalizeAuthSummaries, rpForm?.payee?.authorizationId, rpForm?.recurringPayableLineItems]);

  const vendorValue = payToVendor ? payeeDetails?.vendorInfo?.companyName || '' : '';
  const authValue = !payToVendor
    ? (() => {
        const fm = (payeeDetails?.familyMembers || []).find(f => (f.familyRelationshipTypeId || '').toString().length > 0);
        return fm ? `${fm.firstName || ''} ${fm.lastName || ''}`.trim() : '';
      })()
    : '';

  useEffect(() => {
    setIsDirty(!_.isEqual(shallowNormalize(rpForm), shallowNormalize(rpState)));
  }, [rpForm, rpState]);

  const handleSave = async (req = null) => {
    setIsLoading(true);
    try {
      const payload = shallowNormalize(req ?? rpForm);
      const res = await onUpdateRecurringPayable(payload);
      const next = res?.response || payload;
      setRpForm(next);
      setRpState(next);
      setIsDirty(false);
      return true;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Prompt
        when={true}
        message={() => (!isDirty ? true : 'Make sure to save any unsaved changes. Are you sure you would like to leave?')}
      />
      <HeaderBar
        shouldShowSideDrawer={shouldShowSideDrawer}
        clients={clients}
        payToVendor={payToVendor}
        statusTitle={statusTitle}
        rpInfo={rpForm}
        vendorValue={vendorValue}
        authValue={authValue}
        normalizeAuthSummaries={normalizeAuthSummaries}
        rpStatusId={statusId}
        onCancel={onCancel}
        authorizationDetails={!payToVendor ? payeeDetails?.authorizationDetails : null}
        clientRelocationPolicies={clientRelocationPolicies}
      />
      <Box sx={SECTION_SX}>
        <RecurringPayableScheduleCard
          rpInfo={rpForm}
          updateRpInfo={next => {
            setRpForm(next);
            setIsDirty(true);
          }}
          enumerations={enumerations}
          isReadOnly={
            !(
              String(statusTitle || '').toLowerCase() === 'draft' ||
              String(rpForm?.recurringPayableInformation?.recurringPayableStatusId || '').toLowerCase() === String(ZERO_GUID).toLowerCase()
            )
          }
          history={history}
          voucherDetails={rpForm?.voucherDetails || []}
          statusTitle={statusTitle}
        />
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
          <Grid item xs={12} xl={8}>
            <RecurringPayableInfoCard
              isReadOnly={isReadOnly}
              rpInfo={rpForm}
              updateRpInfo={next => {
                setRpForm(next);
                setIsDirty(true);
              }}
              voucherTypes={voucherTypes}
              batchTypes={batchTypes}
              notPayables={notPayables}
              currencies={currencyOpts}
              frequencies={frequencies}
              banks={banks ?? []}
            />
          </Grid>
          <Grid item xs={12} xl={4}>
            <RecurringPayablePaymentInfoCard
              isReadOnly={isReadOnly}
              rpInfo={rpForm}
              updateRpInfo={next => {
                setRpForm(next);
                setIsDirty(true);
              }}
              banks={banks ?? []}
              addresses={addresses ?? []}
            />
          </Grid>
          <Grid item xs={12}>
            <RecurringPayableLineItemsCard
              isReadOnly={isReadOnly}
              payToVendor={payToVendor}
              recurringPayableId={recurringPayableId}
              recurringPayableInfo={rpState}
              recurringPayableInfoForm={rpForm}
              setRecurringPayableInfoForm={setRpForm}
              setRecurringPayableState={setRpState}
              setIsDirty={setIsDirty}
              expenseCodes={expenseCodes}
              normalizeAuthSummaries={normalizeAuthSummaries}
              enumerations={enumerations}
              recurringPayableLineItems={rpForm?.recurringPayableLineItems ?? []}
              onAddLine={async item => {
                await handleSave();
                const ok = await onSaveLineItem(item);
                if (ok) {
                  return true;
                }
                return false;
              }}
              onUpdateLine={async item => {
                await handleSave();
                const ok = await onSaveLineItem(item);
                return !!ok;
              }}
              onDeleteLine={async lineItemId => {
                await handleSave();
                const ok = await onDeleteLineItem(lineItemId);
                return !!ok;
              }}
              onCopyLineItem={onCopyLineItem}
              updateRecurringPayable={handleSave}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isDirty={isDirty}
              isActive={isActive}
              updateParentRPAttachments={updateParentRPAttachments}
              updateParentRPLineAttachments={updateParentRPLineAttachments}
              setExceptions={setExceptions}
              exceptions={exceptions}
              clientId={rpForm?.recurringPayableInformation?.clientId}
            />
          </Grid>

          <Grid item xs={12}>
            <DynamicFilePreviewCard
              authId={rpForm?.payee?.authorizationId ?? null}
              authOptions={authOptions}
              history={history}
              location={location}
              isReadOnly={true}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default RecurringPayableDetails;
